import { useEffect, useState } from "react";
import { CreditCard, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { api, formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

export function PaymentManager() {
  const [settings, setSettings] = useState(null);
  const [secrets, setSecrets] = useState({ razorpay_key_secret: "", paypal_client_secret: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/payments").then(({ data }) => setSettings(data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        demo_enabled: settings.demo_enabled,
        razorpay_enabled: settings.razorpay_enabled,
        razorpay_key_id: settings.razorpay_key_id,
        paypal_enabled: settings.paypal_enabled,
        paypal_client_id: settings.paypal_client_id,
      };
      if (secrets.razorpay_key_secret) payload.razorpay_key_secret = secrets.razorpay_key_secret;
      if (secrets.paypal_client_secret) payload.paypal_client_secret = secrets.paypal_client_secret;
      const { data } = await api.put("/admin/payments", payload);
      setSettings(data);
      setSecrets({ razorpay_key_secret: "", paypal_client_secret: "" });
      toast.success("Payment settings saved");
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return <div className="py-20 text-center text-zinc-500" data-testid="payments-loading">Loading payment settings...</div>;

  const toggle = (key, value) => setSettings({ ...settings, [key]: value });

  return (
    <section data-testid="admin-payment-manager" className="max-w-3xl">
      <div className="mb-6">
        <h2 className="font-heading text-4xl font-black italic">Payments</h2>
        <p className="mt-2 text-sm text-zinc-500">Enable checkout methods and connect your Razorpay or PayPal credentials. Secrets are stored securely and never shown again.</p>
      </div>

      <div className="space-y-5">
        <div className="glass-panel flex items-center justify-between p-6" data-testid="demo-payment-card">
          <div>
            <p className="font-subheading text-lg font-bold">Demo checkout</p>
            <p className="text-sm text-zinc-500">Saves orders instantly without charging anyone.</p>
          </div>
          <Switch data-testid="demo-enabled-switch" checked={settings.demo_enabled} onCheckedChange={(v) => toggle("demo_enabled", v)} />
        </div>

        <div className="glass-panel p-6" data-testid="razorpay-payment-card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CreditCard className="text-volt" size={20} />
              <div>
                <p className="font-subheading text-lg font-bold">Razorpay</p>
                <p className="text-sm text-zinc-500">UPI, cards, netbanking, wallets (INR).</p>
              </div>
            </div>
            <Switch data-testid="razorpay-enabled-switch" checked={settings.razorpay_enabled} onCheckedChange={(v) => toggle("razorpay_enabled", v)} />
          </div>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <Input data-testid="razorpay-key-id-input" placeholder="Razorpay Key ID (rzp_test_...)" value={settings.razorpay_key_id} onChange={(e) => setSettings({ ...settings, razorpay_key_id: e.target.value })} className="rounded-none bg-white/[0.03]" />
            <Input data-testid="razorpay-secret-input" type="password" placeholder={settings.razorpay_secret_set ? "Secret saved — enter to replace" : "Razorpay Key Secret"} value={secrets.razorpay_key_secret} onChange={(e) => setSecrets({ ...secrets, razorpay_key_secret: e.target.value })} className="rounded-none bg-white/[0.03]" />
          </div>
        </div>

        <div className="glass-panel p-6" data-testid="paypal-payment-card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ShieldCheck className="text-neon" size={20} />
              <div>
                <p className="font-subheading text-lg font-bold">PayPal</p>
                <p className="text-sm text-zinc-500">International cards and PayPal wallets.</p>
              </div>
            </div>
            <Switch data-testid="paypal-enabled-switch" checked={settings.paypal_enabled} onCheckedChange={(v) => toggle("paypal_enabled", v)} />
          </div>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <Input data-testid="paypal-client-id-input" placeholder="PayPal Client ID" value={settings.paypal_client_id} onChange={(e) => setSettings({ ...settings, paypal_client_id: e.target.value })} className="rounded-none bg-white/[0.03]" />
            <Input data-testid="paypal-secret-input" type="password" placeholder={settings.paypal_secret_set ? "Secret saved — enter to replace" : "PayPal Secret"} value={secrets.paypal_client_secret} onChange={(e) => setSecrets({ ...secrets, paypal_client_secret: e.target.value })} className="rounded-none bg-white/[0.03]" />
          </div>
        </div>
      </div>

      <Button data-testid="save-payment-settings-button" onClick={save} disabled={saving} className="kinetic-button mt-6 h-12 w-full rounded-none bg-volt font-bold uppercase tracking-[0.2em] text-black hover:bg-transparent hover:text-volt">
        {saving ? "Saving..." : "Save payment settings"}
      </Button>
    </section>
  );
}
